import React, { useEffect, useState } from 'react';
import { SidePanel } from './SidePanel';
import { Plus, Trash2, X } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import {
  LEGAL_FORMS,
  LegalForm,
  ParticipantForm,
  VEHICLE_KINDS,
  VehicleForm,
  VehicleKind,
  KIND_LABEL,
} from '../types/vehicle';
import { todayLocal } from '../lib/date';

/**
 * 자금원 등록 — 워크플로우 1단계.
 *
 * ⚠️ 묻는 순서가 중요하다. **자금 구분부터** 묻는다.
 *    법적 형태(조합·자문)는 모를 수 있으니 뒤에 두고 건너뛸 수 있게 한다.
 *    모르는 것 때문에 아는 것도 못 적는 일이 없어야 한다.
 */
const EMPTY = (): VehicleForm => ({
  name: '',
  kind: 'entrusted',
  legalForm: '',
  bizNo: '',
  accountName: '',
  startedOn: todayLocal(),
  note: '',
  participants: [{ id: 1, name: '', isManager: false, committedAmount: null }],
});

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const VehicleFormModal: React.FC<Props> = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState<VehicleForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  // 실패하면 창 안에 이유를 보인다 — 던지면 단추가 아무 반응 없이 멈춘다(실사용 테스트 7회차)
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) setForm(EMPTY()); }, [open]);

  if (!open) return null;

  const set = <K extends keyof VehicleForm>(k: K, v: VehicleForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const setPart = (id: number, patch: Partial<ParticipantForm>) =>
    setForm((f) => ({
      ...f,
      participants: f.participants.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    }));

  const addPart = () =>
    setForm((f) => ({
      ...f,
      participants: [
        ...f.participants,
        { id: Math.max(0, ...f.participants.map((p) => p.id)) + 1, name: '', isManager: false, committedAmount: null },
      ],
    }));

  const canSave = form.name.trim().length > 0 && !saving;

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await vehicleApi.create(form);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally {
      setSaving(false);
    }
  };

  const label = 'block text-xs font-medium text-slate-500 mb-1';
  const input =
    'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">자금원 추가</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* 1. 자금 구분 — 이것부터 묻는다 */}
          <div>
            <label className={label}>자금 구분</label>
            <div className="grid grid-cols-2 gap-2">
              {VEHICLE_KINDS.map((k: VehicleKind) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => set('kind', k)}
                  className={`text-left px-3 py-2.5 rounded-lg border transition ${
                    form.kind === k
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <div className={`text-sm font-semibold ${form.kind === k ? 'text-indigo-700' : 'text-slate-900'}`}>
                    {KIND_LABEL[k]}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={label} htmlFor="v-name">자금원명</label>
            <input
              id="v-name" className={input} value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder={form.kind === 'own' ? '예) GRK파트너스 고유계정' : '예) 지알케이(GRK) 기술 개인투자조합 1호'}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="v-start">운용 개시일</label>
              <input id="v-start" type="date" className={input} value={form.startedOn}
                onChange={(e) => set('startedOn', e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="v-acc">예금주(계좌 명의)</label>
              <input id="v-acc" className={input} value={form.accountName}
                onChange={(e) => set('accountName', e.target.value)} placeholder="미확인 시 공란" />
            </div>
          </div>

          {/* 2. 법적 형태 — 몰라도 넘어갈 수 있어야 한다 */}
          {form.kind === 'entrusted' && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <label className={label}>운용 형태 <span className="text-slate-400">— 미확인 시 「미정」</span></label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => set('legalForm', '')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                    form.legalForm === ''
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  미정
                </button>
                {LEGAL_FORMS.map((f: LegalForm) => (
                  <button
                    key={f} type="button" onClick={() => set('legalForm', f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      form.legalForm === f
                        ? 'bg-slate-900 border-slate-900 text-white'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              {form.legalForm === '조합' && (
                <div className="mt-3">
                  <label className={label} htmlFor="v-biz">조합 고유번호</label>
                  <input id="v-biz" className={input} value={form.bizNo}
                    onChange={(e) => set('bizNo', e.target.value)} placeholder="000-00-00000" />
                </div>
              )}
            </div>
          )}

          {/* 3. 참여자 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className={label + ' mb-0'}>출자자 <span className="text-slate-400">— 조합은 GP · LP, 자문 · 일임은 위탁 기업</span></span>
              <button type="button" onClick={addPart}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50">
                <Plus size={11} /> 추가
              </button>
            </div>
            <div className="space-y-2">
              {form.participants.map((p) => (
                <div key={p.id} className="flex items-center gap-2">
                  <input
                    className={input + ' flex-1 min-w-0'} value={p.name}
                    onChange={(e) => setPart(p.id, { name: e.target.value })}
                    placeholder="출자자명 (법인 · 개인)"
                  />
                  <input
                    type="number" min={0} step={10000000}
                    // input 의 w-full 과 겹치면 w-full 이 이겨 이름 칸이 찌그러진다 — 폭을 따로 준다
                    className={input.replace('w-full', 'w-40 flex-none') + ' text-right'}
                    value={p.committedAmount ?? ''}
                    onChange={(e) => setPart(p.id, { committedAmount: e.target.value === '' ? null : Number(e.target.value) })}
                    placeholder="출자약정액"
                  />
                  <label className="flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap">
                    <input type="checkbox" checked={p.isManager}
                      onChange={(e) => setPart(p.id, { isManager: e.target.checked })} />
                    GP
                  </label>
                  {form.participants.length > 1 && (
                    <button type="button"
                      onClick={() => setForm((f) => ({ ...f, participants: f.participants.filter((x) => x.id !== p.id) }))}
                      className="p-1 rounded text-slate-300 hover:text-red-600 hover:bg-red-50">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className={label} htmlFor="v-note">비고</label>
            <textarea id="v-note" className={input + ' h-16 resize-none'} value={form.note}
              onChange={(e) => set('note', e.target.value)} placeholder="결성 경위 · 근거 문서 등" />
          </div>
        </div>

        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
            취소
          </button>
          <button onClick={save} disabled={!canSave}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? '저장 중...' : '등록'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};
